import { Workbook } from "exceljs";
import {
	VisitAnalyteResponse,
	VisitDetailFilters,
	VisitDetailRow,
	reportsService,
} from "../../services/reports.service";
import { addCoverSheet } from "./cover-sheet";
import { applyHeaderStyle, currencyFmt, filenameSlug } from "./workbook-styles";
import { BuiltWorkbook } from "./build-workbook";

export interface VisitDetailWorkbookFilters extends VisitDetailFilters {
	programName?: string;
	// Currency symbol from the account's `currency` setting (e.g. "$"), applied
	// to the Incentive Earned column so the .xlsx matches the on-screen / PDF.
	currencyMask?: string;
	// Human-readable filter values for the cover sheet (optional).
	orgUnitFilter?: string;
	researcherFilter?: string;
}

/**
 * SIMS-7588 — Build the Visit Detail Excel export ENTIRELY IN THE BROWSER.
 *
 * The backend `/reports/visit-detail/export` endpoint returned the workbook as a
 * binary body that had to cross API Gateway + the edge-optimized CloudFront
 * custom domain. That path base64-encodes the body and the base64 arrived at
 * the browser UNDECODED, so Excel rejected the file. Building the workbook
 * client-side (from JSON) avoids any binary crossing API Gateway.
 *
 * Structure mirrors the PDF (VisitDetailTab.handlePdf), which the report is
 * expected to match: two sheets —
 *   - "Visits":   one row per visit (visit-level fields).
 *   - "Analytes": one row per visit-analyte, only for visits that captured cup
 *                 data (event.cupsAndAnalytes + cupId). Empty when none.
 */
export const buildVisitDetailWorkbook = async (
	filters: VisitDetailWorkbookFilters,
): Promise<BuiltWorkbook> => {
	const rows = await reportsService.fetchAllVisitDetail({
		accountId: filters.accountId,
		programId: filters.programId,
		orgUnitIds: filters.orgUnitIds,
		payerIds: filters.payerIds,
		groupLabelIds: filters.groupLabelIds,
		providerId: filters.providerId,
		startDate: filters.startDate,
		endDate: filters.endDate,
		cin: filters.cin,
	});

	const programName = filters.programName ?? "Program";
	const earnedFmt = currencyFmt(filters.currencyMask ?? "$");

	const workbook = new Workbook();
	workbook.creator = "SIMS Reporting";
	workbook.created = new Date();

	addCoverSheet(workbook, {
		programName,
		exportedAt: new Date(),
		startDate: filters.startDate,
		endDate: filters.endDate,
		orgUnitFilter: filters.orgUnitFilter,
		researcherFilter: filters.researcherFilter,
	});

	// --- Sheet 1: Visits (one row per visit) — mirrors PDF "Visits" section. ---
	const visits = workbook.addWorksheet("Visits");
	visits.columns = [
		{ header: "Visit Date", key: "visitDate", width: 14 },
		{ header: "Visit Number", key: "visitNumber", width: 14 },
		{ header: "Member Name", key: "member", width: 24 },
		{ header: "CIN", key: "cin", width: 14 },
		{ header: "Org Unit", key: "orgUnit", width: 18 },
		{ header: "Provider", key: "provider", width: 20 },
		{ header: "Event Name", key: "eventName", width: 18 },
		{ header: "Visit Result", key: "result", width: 16 },
		{ header: "Incentive Earned", key: "earned", width: 16 },
		{ header: "Payer Plan", key: "payerPlanName", width: 18 },
		{ header: "Payer Plan ID", key: "payerPlanNumber", width: 14 },
	];
	applyHeaderStyle(visits.getRow(1));
	for (const row of rows) {
		const r = visits.addRow({
			visitDate: row.visitDate ? row.visitDate.slice(0, 10) : "",
			visitNumber: row.visitNumber ?? "",
			member: row.member ?? "",
			cin: row.cin ?? "",
			orgUnit: row.orgUnit ?? "",
			provider: row.provider ?? "",
			eventName: row.eventName ?? "",
			result: row.result ?? "",
			earned: row.earned,
			payerPlanName: row.payerPlanName ?? "",
			payerPlanNumber: row.payerPlanNumber ?? "",
		});
		r.getCell("earned").numFmt = earnedFmt;
	}

	// --- Sheet 2: Analytes (one row per visit-analyte) — mirrors PDF "Analytes". ---
	const analyteSheet = workbook.addWorksheet("Analytes");
	analyteSheet.columns = [
		{ header: "Visit Date", key: "visitDate", width: 14 },
		{ header: "Visit Number", key: "visitNumber", width: 14 },
		{ header: "Member Name", key: "member", width: 24 },
		{ header: "CIN", key: "cin", width: 14 },
		{ header: "Event Name", key: "eventName", width: 18 },
		{ header: "Cup Name", key: "cupName", width: 18 },
		{ header: "Analyte Name", key: "analyteName", width: 16 },
		{ header: "Analyte Result", key: "analyteResult", width: 16 },
		{ header: "Payer Plan", key: "payerPlanName", width: 18 },
		{ header: "Payer Plan ID", key: "payerPlanNumber", width: 14 },
	];
	applyHeaderStyle(analyteSheet.getRow(1));

	// Resolve analyte drill-downs once per visit (cache by enrollment:udtResult).
	const analytesCache = new Map<string, VisitAnalyteResponse | null>();
	const analytesFor = async (
		row: VisitDetailRow,
	): Promise<VisitAnalyteResponse | null> => {
		if (!row.hasAnalyteDetails) return null;
		const key = `${row.enrollmentId}:${row.udtResultId}`;
		const cached = analytesCache.get(key);
		if (cached !== undefined) return cached;
		try {
			const data = await reportsService.getVisitAnalytes(
				filters.accountId,
				row.enrollmentId,
				row.udtResultId,
			);
			analytesCache.set(key, data);
			return data;
		} catch {
			analytesCache.set(key, null);
			return null;
		}
	};

	for (const row of rows) {
		const analyteData = await analytesFor(row);
		if (!analyteData || analyteData.analytes.length === 0) continue;
		for (const a of analyteData.analytes) {
			analyteSheet.addRow({
				visitDate: row.visitDate ? row.visitDate.slice(0, 10) : "",
				visitNumber: row.visitNumber ?? "",
				member: row.member ?? "",
				cin: row.cin ?? "",
				eventName: row.eventName ?? "",
				cupName: analyteData.cupName ?? "",
				analyteName: a.shortName ?? "",
				analyteResult: a.positive ? "Positive" : "Negative",
				payerPlanName: row.payerPlanName ?? "",
				payerPlanNumber: row.payerPlanNumber ?? "",
			});
		}
	}

	const arrayBuffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([arrayBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const filename = `visit_detail_${filenameSlug(programName)}.xlsx`;
	return { blob, filename };
};
